import {
  HHBaseTable,
  HHButton,
  HHCircularProgress,
  HHLink,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ErrorIcon from '@mui/icons-material/Error';
import { useEffect, useState } from 'react';
import { useGetTemporalWorkflowLazyQuery } from '../../billing-tool/types';

export enum UploadRunStatus {
  RUNNING = 'RUNNING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELED = 'CANCELED',
  TERMINATED = 'TERMINATED',
  CONTINUED_AS_NEW = 'CONTINUED_AS_NEW',
  TIMED_OUT = 'TIMED_OUT',
}

const getFileUploadStatusIcon = (
  status: UploadRunStatus,
  failureMessage: string | null,
): JSX.Element => {
  switch (status) {
    case UploadRunStatus.RUNNING:
      return (
        <HHCircularProgress
          styleAs="secondary"
          title="Run in progress"
          sx={{ verticalAlign: 'middle' }}
          size={20}
        />
      );
    case UploadRunStatus.COMPLETED:
      return (
        <CheckBoxIcon
          color="primary"
          titleAccess="Run has successfully completed"
          sx={{ verticalAlign: 'middle' }}
        />
      );
    case UploadRunStatus.FAILED:
      return (
        <ErrorIcon
          color="error"
          titleAccess={
            failureMessage
              ? `Run has failed: ${failureMessage}`
              : 'Run has failed'
          }
          sx={{ verticalAlign: 'middle' }}
        />
      );
    case UploadRunStatus.CANCELED:
      return (
        <ErrorIcon
          color="error"
          titleAccess="Run has been canceled"
          sx={{ verticalAlign: 'middle' }}
        />
      );
    case UploadRunStatus.TERMINATED:
      return (
        <ErrorIcon
          color="error"
          titleAccess={
            failureMessage
              ? `Run has been terminated: ${failureMessage}`
              : 'Run has been terminated'
          }
          sx={{ verticalAlign: 'middle' }}
        />
      );
    case UploadRunStatus.CONTINUED_AS_NEW:
      return (
        <ErrorIcon
          color="error"
          titleAccess="Run has been continued as new"
          sx={{ verticalAlign: 'middle' }}
        />
      );
    case UploadRunStatus.TIMED_OUT:
      return (
        <ErrorIcon
          color="error"
          titleAccess={
            failureMessage
              ? `Run has timed out: ${failureMessage}`
              : 'Run has timed out'
          }
          sx={{ verticalAlign: 'middle' }}
        />
      );
    default:
      return (
        <HHCircularProgress
          styleAs="secondary"
          title="Run in progress"
          sx={{ verticalAlign: 'middle' }}
          size={20}
        />
      );
  }
};

export interface UploadStatusType {
  workflowId: string;
  fileName: string;
  uploadedAt: string;
  fileType: string;
  fileLink: string;
  status: UploadRunStatus;
  failureMessage: string | null;
}

const CsvUploadStatus = (): JSX.Element => {
  const [uploadStatus, setUploadStatus] = useState<UploadStatusType[]>([]);
  const [getTemporalWorkflow, { loading }] = useGetTemporalWorkflowLazyQuery();

  useEffect(() => {
    setUploadStatus(
      JSON.parse(localStorage.getItem('fileUploadStatus') || '[]'),
    );
  }, [setUploadStatus]);

  const fetchUploadStatus = (workflowId: string): void => {
    getTemporalWorkflow({
      variables: {
        workflowId,
      },
      fetchPolicy: 'no-cache',
    })
      .then(({ data }) => {
        if (!data?.getTemporalWorkflow) {
          throw new Error('Failed to fetch upload status');
        }

        const urs = uploadStatus.find(
          status => status.workflowId === workflowId,
        );
        // istanbul ignore next
        if (!urs) {
          // this will not happen because we are fetching the status for the current workflow
          throw new Error('Upload status not found');
        }
        urs.status = data.getTemporalWorkflow.status as UploadRunStatus;
        urs.failureMessage = data.getTemporalWorkflow.failureMessage || null;
        setUploadStatus([...uploadStatus]);
        localStorage.setItem('fileUploadStatus', JSON.stringify(uploadStatus));
      })
      .catch(error => {
        console.error('Error fetching upload status:', error);
      });
  };

  return (
    <HHStack direction="column">
      {uploadStatus.length > 0 ? (
        <HHBaseTable
          tableCellStyles={{ verticalAlign: 'middle' }}
          headerRowBottomBorder="1px solid rgba(0, 0, 0, 0.12)"
          bodyRowBorder={{
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          }}
          bodyTypographyVariant="body2"
          tableHeaders={[
            {
              value: 'File Name or Link',
              align: 'left',
            },
            { value: 'Uploaded At', align: 'left' },
            { value: 'File Type', align: 'left' },
            { value: 'Status', align: 'left' },
            { value: '', align: 'right' },
          ]}
          bodyContent={uploadStatus.map(status => ({
            fileNameOrLink: {
              value: status.fileLink ? (
                <HHLink
                  href={status.fileLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Google Sheet
                </HHLink>
              ) : (
                status.fileName
              ),
            },
            uploadedAt: {
              value: status.uploadedAt,
            },
            fileType: {
              value: status.fileType,
            },
            runStatus: {
              value: getFileUploadStatusIcon(
                status.status,
                status.failureMessage,
              ),
            },
            actions: {
              value: (
                <HHButton
                  hhVariant="variant-bypass"
                  variant="outlined"
                  size="small"
                  color="primary"
                  disabled={loading}
                  onClick={(): void => {
                    fetchUploadStatus(status.workflowId);
                  }}
                >
                  check
                </HHButton>
              ),
            },
          }))}
        />
      ) : (
        <HHTypography hhVariant="muted">No runs found</HHTypography>
      )}
    </HHStack>
  );
};

export default CsvUploadStatus;
